/**
 * PURE finance aggregation (F3): reports by range / fund / category over the
 * records the sync already authorized (minor units, integers).
 * No dependency on the DB or the DOM — unit-testable.
 */

export interface FinanceRecordLike {
  id: string;
  kind: string | null;
  amount_minor: number | null;
  currency: string | null;
  occurred_at: string | null;
  created_at?: string | null;
  fund_id: string | null;
  category_id: string | null;
  church_id?: string | null;
}

export interface FinanceFilter {
  /** Inclusive, ms epoch. */
  fromMs?: number | null;
  /** Inclusive, ms epoch. */
  toMs?: number | null;
  fundId?: string | null;
  categoryId?: string | null;
  churchId?: string | null;
}

export interface FinanceBucket {
  key: string;
  incomeMinor: number;
  expenseMinor: number;
  netMinor: number;
  count: number;
}

export interface FinanceSummary {
  incomeMinor: number;
  expenseMinor: number;
  netMinor: number;
  count: number;
  byFund: FinanceBucket[];
  byCategory: FinanceBucket[];
  /** `YYYY-MM` (UTC) in chronological order. */
  byMonth: FinanceBucket[];
}

/** Effective date of the record: `occurred_at` and, if missing, `created_at`. */
export function recordInstantMs(record: FinanceRecordLike): number | null {
  const raw = record.occurred_at ?? record.created_at ?? null;
  if (!raw) {
    return null;
  }
  const ms = Date.parse(raw.includes("T") ? raw : `${raw.replace(" ", "T")}Z`);
  return Number.isNaN(ms) ? null : ms;
}

/** Applies the report filter (inclusive range + fund + category). */
export function filterFinanceRecords<T extends FinanceRecordLike>(
  records: readonly T[],
  filter: FinanceFilter = {},
): T[] {
  return records.filter((r) => {
    if (filter.churchId && r.church_id !== filter.churchId) {
      return false;
    }
    if (filter.fundId && r.fund_id !== filter.fundId) {
      return false;
    }
    if (filter.categoryId && r.category_id !== filter.categoryId) {
      return false;
    }
    if (filter.fromMs != null || filter.toMs != null) {
      const ms = recordInstantMs(r);
      if (ms === null) {
        return false;
      }
      if (filter.fromMs != null && ms < filter.fromMs) {
        return false;
      }
      if (filter.toMs != null && ms > filter.toMs) {
        return false;
      }
    }
    return true;
  });
}

const UNASSIGNED_KEY = "";

function addToBucket(map: Map<string, FinanceBucket>, key: string, r: FinanceRecordLike): void {
  const bucket = map.get(key) ?? {
    key,
    incomeMinor: 0,
    expenseMinor: 0,
    netMinor: 0,
    count: 0,
  };
  const amount = r.amount_minor ?? 0;
  if (r.kind === "expense") {
    bucket.expenseMinor += amount;
    bucket.netMinor -= amount;
  } else {
    bucket.incomeMinor += amount;
    bucket.netMinor += amount;
  }
  bucket.count += 1;
  map.set(key, bucket);
}

/** UTC `YYYY-MM` of the record (monthly report bucket). */
export function monthKey(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

/**
 * Finance summary over ALREADY filtered records or with an inline filter:
 * income/expense/net totals + breakdowns by fund, category and month.
 * Records without fund/category are grouped under the empty key `""`.
 */
export function financeSummary(
  records: readonly FinanceRecordLike[],
  filter: FinanceFilter = {},
): FinanceSummary {
  const rows = filterFinanceRecords(records, filter);
  let incomeMinor = 0;
  let expenseMinor = 0;
  const byFund = new Map<string, FinanceBucket>();
  const byCategory = new Map<string, FinanceBucket>();
  const byMonth = new Map<string, FinanceBucket>();
  for (const r of rows) {
    const amount = r.amount_minor ?? 0;
    if (r.kind === "expense") {
      expenseMinor += amount;
    } else {
      incomeMinor += amount;
    }
    addToBucket(byFund, r.fund_id ?? UNASSIGNED_KEY, r);
    addToBucket(byCategory, r.category_id ?? UNASSIGNED_KEY, r);
    const ms = recordInstantMs(r);
    if (ms !== null) {
      addToBucket(byMonth, monthKey(ms), r);
    }
  }
  return {
    incomeMinor,
    expenseMinor,
    netMinor: incomeMinor - expenseMinor,
    count: rows.length,
    byFund: [...byFund.values()].toSorted((a, b) => b.netMinor - a.netMinor),
    byCategory: [...byCategory.values()].toSorted(
      (a, b) => b.incomeMinor + b.expenseMinor - (a.incomeMinor + a.expenseMinor),
    ),
    byMonth: [...byMonth.values()].toSorted((a, b) => a.key.localeCompare(b.key)),
  };
}
